import { deserialize, DocumentObject, serialize } from '@jsonapiworld/jsonapi-serializer';
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { deleteVATBook, fetchAllVATBooks, updateVATBook } from '~/api/bookkeeping/vat-books';
import { backendApiGateway } from '~/client/backend';
import { queryClient } from '~/client/queryClient';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import useExceptionLogger from '~/hooks/useExceptionLogger';
import { assertIsDefined, isDefined } from '~/types/typeGuards';
import {
  CreateVATBookParameters,
  UpdateVATBookParameters,
  VATBook,
  VATBookFilters,
  VATBookQueryKeys,
  VATBookSettlement,
} from '~/types/vat-bookkeeping';
import { buildJsonAPIQueryParams, jsonAPIExtraQueryBuilder } from '~/utils/jsonapi-query';

export const useVATBooks = () => {
  const companyId = useActiveCompanyId();

  return useSuspenseQuery({
    queryKey: VATBookQueryKeys.all(companyId),
    queryFn: () => fetchAllVATBooks(companyId),
  });
};

export const useVATBooksQuery = (filters: VATBookFilters = {}) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: VATBookQueryKeys.list(companyId, filters),
    queryFn: () => fetchAllVATBooks(companyId, filters),
    enabled: !!companyId,
  });
};

const createVATBook = async (params: CreateVATBookParameters) => {
  const payload = serialize(
    {
      company: {
        id: params.companyId,
      },
      bookType: params.bookType,
      name: params.name,
      year: params.year,
      fallbackVatBook: params.fallbackVatBook ?? false,
      criteria: params.criteria ?? [],
    },
    'vat-book',
    {
      relationships: ['company'],
    },
  );

  const { data } = await backendApiGateway.post<DocumentObject>('api/v1/vat-books', payload);
  return deserialize<VATBook>(data) as VATBook;
};

type CreateVATBookOptions = Omit<CreateVATBookParameters, 'companyId'>;

export const useCreateVATBook = () => {
  const companyId = useActiveCompanyId();
  const { captureException } = useExceptionLogger();
  return useMutation({
    mutationFn: (options: CreateVATBookOptions) => createVATBook({ companyId, ...options }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: VATBookQueryKeys.all(companyId) }),
    onError: (error) => captureException(error),
  });
};

export const useUpdateVATBook = () => {
  const companyId = useActiveCompanyId();
  const { captureException } = useExceptionLogger();
  return useMutation({
    mutationFn: (params: UpdateVATBookParameters) => updateVATBook(params),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: VATBookQueryKeys.all(companyId) }),
    onError: (error) => captureException(error),
  });
};

export const useDeleteVATBook = () => {
  const companyId = useActiveCompanyId();
  const { captureException } = useExceptionLogger();
  return useMutation({
    mutationFn: (id: VATBook['id']) => deleteVATBook(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: VATBookQueryKeys.all(companyId) }),
    onError: (error) => captureException(error),
  });
};

const exportVATBooks = async (vatReportingPeriodId: string) => {
  const paramsBuilder = jsonAPIExtraQueryBuilder();
  paramsBuilder.add('vatReportingPeriodId', vatReportingPeriodId);

  const queryParams = buildJsonAPIQueryParams(paramsBuilder);
  const { data } = await backendApiGateway.get<{ success: boolean; tracing_id: string }>('api/v1/vat-books/export', {
    params: queryParams,
  });
  return data;
};

export const useExportVATBooks = () => {
  const { captureException } = useExceptionLogger();
  return useMutation({
    mutationFn: (vatReportingPeriodId: string) => exportVATBooks(vatReportingPeriodId),
    onError: (error) => captureException(error),
  });
};

const fetchSettlement = async (vatReportingPeriodId: string): Promise<VATBookSettlement> => {
  const paramsBuilder = jsonAPIExtraQueryBuilder();
  paramsBuilder.add('vatReportingPeriodId', vatReportingPeriodId);

  const queryParams = buildJsonAPIQueryParams(paramsBuilder);
  const { data } = await backendApiGateway.get<{ data: VATBookSettlement }>('api/v1/vat-books/vat-settlement', {
    params: queryParams,
  });
  return data.data;
};

export const useSettlement = (vatReportingPeriodId: string | undefined) => {
  return useQuery({
    queryKey: VATBookQueryKeys.settlement(vatReportingPeriodId),
    queryFn: () => {
      assertIsDefined(vatReportingPeriodId, 'vatReportingPeriodId must be defined to fetch settlement');
      return fetchSettlement(vatReportingPeriodId);
    },
    enabled: isDefined(vatReportingPeriodId),
  });
};
